import { useState, useCallback } from "react";
import { ValidationRule, validateField } from "../utils/validation";

// ponytail: constraint is `object` not `Record<string, unknown>` — plain caller
// interfaces (incl. frozen test fixtures) lack an index signature and fail the
// `unknown` record constraint. `object` accepts them and is not `any`.
export interface UseFormValidationProps<T extends object> {
  initialValues: T;
  validationRules: Partial<Record<keyof T, ValidationRule>>;
  onSubmit: (_values: T) => void | Promise<void>;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
}

export interface FormState<T> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  isSubmitting: boolean;
  isValid: boolean;
}

export interface FormHelpers<T> {
  setValue: (_field: keyof T, _value: T[keyof T]) => void;
  setValues: (_values: Partial<T>) => void;
  setError: (_field: keyof T, _error: string) => void;
  setErrors: (_errors: Partial<Record<keyof T, string>>) => void;
  clearError: (_field: keyof T) => void;
  clearErrors: () => void;
  setTouched: (_field: keyof T, _touched?: boolean) => void;
  setFieldTouched: (_field: keyof T, _touched?: boolean) => void;
  validateField: (_field: keyof T) => string | null;
  validateForm: () => boolean;
  resetForm: (_values?: T) => void;
  handleChange: (
    _field: keyof T
  ) => (_e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleBlur: (
    _field: keyof T
  ) => (_e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleSubmit: (_e: React.FormEvent) => void;
}

export const useFormValidation = <T extends object>({
  initialValues,
  validationRules,
  onSubmit,
  validateOnChange = true,
  validateOnBlur = true,
}: UseFormValidationProps<T>): FormState<T> & FormHelpers<T> => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate if form is valid
  const isValid = Object.keys(errors).length === 0 && Object.keys(touched).length > 0;

  // Validate a single field
  const validateSingleField = useCallback(
    (field: keyof T, currentValues?: T): string | null => {
      const rule = validationRules[field];
      if (!rule) return null;

      const valuesToUse = currentValues || values;
      const value = valuesToUse[field];
      const fieldName = String(field);

      // Check required field first
      if (rule.required && (!value || (typeof value === "string" && value.trim() === ""))) {
        return `${fieldName} is required`;
      }

      // Skip other validations if field is empty and not required
      if (!value || (typeof value === "string" && value.trim() === "")) {
        return null;
      }

      // Handle special case for confirm password or similar fields that need to compare with other values
      if (rule.custom) {
        return rule.custom(value, valuesToUse as Record<string, unknown>);
      }

      return validateField(value, rule, fieldName);
    },
    [values, validationRules]
  );

  // Validate entire form
  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof T, string>> = {};

    Object.keys(validationRules).forEach((field) => {
      const fieldKey = field as keyof T;
      const error = validateSingleField(fieldKey, values);
      if (error) {
        newErrors[fieldKey] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [validationRules, values, validateSingleField]);

  // Set single value
  const setValue = useCallback(
    (field: keyof T, value: T[keyof T]) => {
      const newValues = { ...values, [field]: value };
      setValues(newValues);

      // Clear error when value changes
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }

      // Validate on change if enabled
      if (validateOnChange && touched[field]) {
        const error = validateSingleField(field, newValues);
        if (error) {
          setErrors((prev) => ({ ...prev, [field]: error }));
        }
      }

      // Check for dependent fields that need re-validation
      // For example, if password changes, re-validate confirmPassword
      if (validateOnChange) {
        Object.keys(validationRules).forEach((ruleField) => {
          const ruleFieldKey = ruleField as keyof T;
          const rule = validationRules[ruleFieldKey];

          // If this field has a custom validation that might depend on other fields
          // and it's not the field we just changed, and it's been touched
          if (rule?.custom && ruleFieldKey !== field && touched[ruleFieldKey]) {
            const error = validateSingleField(ruleFieldKey, newValues);

            if (error) {
              setErrors((prev) => ({ ...prev, [ruleFieldKey]: error }));
            } else {
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[ruleFieldKey];
                return newErrors;
              });
            }
          }
        });
      }
    },
    [errors, touched, validateOnChange, validateSingleField, validationRules, values]
  );

  // Set multiple values
  const setValuesHelper = useCallback((newValues: Partial<T>) => {
    setValues((prev) => ({ ...prev, ...newValues }));
  }, []);

  // Set single error
  const setError = useCallback((field: keyof T, error: string) => {
    setErrors((prev) => ({ ...prev, [field]: error }));
  }, []);

  // Set multiple errors
  const setErrorsHelper = useCallback((newErrors: Partial<Record<keyof T, string>>) => {
    setErrors(newErrors);
  }, []);

  // Clear single error
  const clearError = useCallback((field: keyof T) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Set field as touched
  const setFieldTouched = useCallback((field: keyof T, isTouched: boolean = true) => {
    setTouched((prev) => ({ ...prev, [field]: isTouched }));
  }, []);

  // Reset form
  const resetForm = useCallback(
    (newValues?: T) => {
      setValues(newValues || initialValues);
      setErrors({});
      setTouched({});
      setIsSubmitting(false);
    },
    [initialValues]
  );

  // Handle input change
  const handleChange = useCallback(
    (field: keyof T) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { value, type } = e.target;
        const processedValue = type === "number" ? (value === "" ? "" : Number(value)) : value;
        setValue(field, processedValue as T[keyof T]);
      },
    [setValue]
  );

  // Handle input blur
  const handleBlur = useCallback(
    (field: keyof T) => () => {
      setFieldTouched(field, true);

      if (validateOnBlur) {
        const error = validateSingleField(field, values);
        if (error) {
          setError(field, error);
        } else {
          clearError(field);
        }
      }
    },
    [validateOnBlur, validateSingleField, setFieldTouched, setError, clearError, values]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Mark all fields as touched
      const allTouched: Partial<Record<keyof T, boolean>> = {};
      Object.keys(validationRules).forEach((field) => {
        allTouched[field as keyof T] = true;
      });
      setTouched(allTouched);

      // Validate form
      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);
      try {
        await onSubmit(values);
      } catch (error) {
        console.error("Form submission error:", error);
        // You might want to set a general error here
      } finally {
        setIsSubmitting(false);
      }
    },
    [values, validationRules, validateForm, onSubmit]
  );

  return {
    // State
    values,
    errors,
    touched,
    isSubmitting,
    isValid,

    // Helpers
    setValue,
    setValues: setValuesHelper,
    setError,
    setErrors: setErrorsHelper,
    clearError,
    clearErrors,
    setTouched: setFieldTouched,
    setFieldTouched,
    validateField: validateSingleField,
    validateForm,
    resetForm,
    handleChange,
    handleBlur,
    handleSubmit,
  };
};
